// SAM-1095 — pieces shared by the full-page Article and Ad editors and their Preview pages (Tailwind only).
import React from 'react';
import { ArrowLeft } from 'lucide-react';

export const Card: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <section className="flex min-w-0 flex-col gap-3.5 rounded-lg bg-white p-5 shadow-sm">
    <div>
      <h2 className="m-0 text-base font-semibold">{title}</h2>
      {subtitle && <div className="mt-0.5 text-[13px] text-gray-500">{subtitle}</div>}
    </div>
    {children}
  </section>
);

export const Field: React.FC<{ label: string; htmlFor?: string; hint?: string; error?: string; required?: boolean; children: React.ReactNode }> = ({ label, htmlFor, hint, error, required, children }) => (
  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
    <label htmlFor={htmlFor} className="text-xs font-semibold text-gray-500">
      {label}{required && ' *'}
    </label>
    {children}
    {hint && !error && <span className="text-xs leading-snug text-gray-500">{hint}</span>}
    {error && <span className="text-xs text-red-600">{error}</span>}
  </div>
);

export const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900';
export const inputClass = selectClass;

export const Segmented: React.FC<{ label: string; value: string; options: { id: string; text: string }[]; onChange: (id: string) => void; disabled?: boolean }> = ({ label, value, options, onChange, disabled }) => (
  <div role="group" aria-label={label} className="inline-flex overflow-hidden rounded-lg border border-gray-300">
    {options.map((o) => (
      <button
        key={o.id}
        type="button"
        aria-pressed={value === o.id}
        disabled={disabled}
        onClick={() => onChange(o.id)}
        className="h-8 border-0 px-3 text-[13px] font-semibold disabled:opacity-60"
        style={value === o.id ? { background: 'var(--tenant-primary, #0B5C4D)', color: '#fff' } : { background: '#fff', color: '#374151' }}
      >
        {o.text}
      </button>
    ))}
  </div>
);

export const Chip: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode }> = ({ on, onClick, disabled, children }) => (
  <button
    type="button"
    aria-pressed={on}
    disabled={disabled}
    onClick={onClick}
    className="rounded-full border px-3 py-1 text-[13px] font-semibold disabled:opacity-60"
    style={on ? { background: '#E7F1EE', color: '#0B5C4D', borderColor: '#E7F1EE' } : { background: '#fff', color: '#6B7280', borderColor: '#D1D5DB' }}
  >
    {children}
  </button>
);

export const btn = 'inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-lg border px-4 text-sm font-semibold disabled:opacity-50';
export const btnGhost = `${btn} border-gray-300 bg-white text-gray-700`;
export const btnPrimary = `${btn} text-white`;
export const primaryStyle: React.CSSProperties = { background: 'var(--tenant-primary, #0B5C4D)', borderColor: 'var(--tenant-primary, #0B5C4D)' };

/** Back link, title, badge and the actions on the right; children below it (the page body). */
export const PageHead: React.FC<{ back: string; onBack: () => void; title: string; badge?: React.ReactNode; aside?: React.ReactNode; actions?: React.ReactNode }> = ({ back, onBack, title, badge, aside, actions }) => (
  <div className="flex flex-col gap-3 px-8 pt-5">
    <button type="button" onClick={onBack} className="flex items-center gap-1.5 self-start border-0 bg-transparent p-0 text-[13px] font-semibold" style={{ color: 'var(--tenant-primary, #0B5C4D)' }}>
      <ArrowLeft size={14} aria-hidden /> {back}
    </button>
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <h1 className="m-0 truncate text-2xl font-bold text-gray-900">{title}</h1>
        {badge}
        {aside}
      </div>
      <div className="flex shrink-0 gap-2.5">{actions}</div>
    </div>
  </div>
);
